<template>
  <div>
    <TWLoadingSpinner v-if="loading" />
    <PublicPageTemplate v-else>
      <template v-slot:content>
        <PublicPageHeader :title="$t('authentication.setupTitle')" :info="infoText" :showHR="true" />
        <PublicPageContent>
          <template v-slot:content>
            <div class="flex flex-col sm:flex-row justify-between">
              <div class="sm:w-1/2 md:w-2/3 text-sm mr-base">
                <b class="text-sm text-black">{{ $t('authentication.setupStepsTitle') }}</b>
                <ol class="pl-base mb-none text-black font-normal">
                  <li>{{ $t('authentication.setupStep1') }}</li>
                  <li>{{ $t('authentication.setupStep2') }}</li>
                  <li>{{ $t('authentication.setupStep3') }}</li>
                </ol>
              </div>
              <div class="w-qrLarge h-qrLarge sm:w-qrSmall sm:h-qrSmall self-center mt-sm sm:mt-none">
                <div class="w-full h-full self-center sm:self-start">
                  <img :src="userMfa.qr_code" />
                </div>
              </div>
            </div>
          </template>
        </PublicPageContent>
        <AuthCodeInput @clear-error="errorMessage = null" @auth-code-changed="setAuthCode" :errorMessage="errorMessage" :successDisplay="successDisplay" :title="$t('authentication.codeInputTitle')" />
        <div class="text-center text-sm flex flex-col">
          <hr class="mt-base mb-base" />
          <span class="font-normal">{{ $t('authentication.troubleScanningEnterKey') }}</span>
          <div class="mt-sm">
            <span class="font-normal text-primary user-select">{{ userMfa.secret }} </span>
          </div>
          <a href="https://signable.help/two-factor-authentication/" data-ec="setup_two_factor_authentication" data-et="link_to_help_site" target="_blank" class="text-black font-normal hover:text-primary mt-sm">{{ $t('authentication.needHelpLink') }}</a>
          <a href="#" data-ec="setup_two_factor_authentication" data-et="logout" @click="logout" class="text-black font-normal hover:text-primary mt-sm">{{ $t('functional.logOutVerb') }}</a>
        </div>
      </template>
    </PublicPageTemplate>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TWLoadingSpinner from '@/components/tailwind/LoadingSpinner.vue';
import PublicPageTemplate from '@/components/tailwind/publicPages/PublicPageTemplate.vue';
import PublicPageHeader from '@/components/tailwind/publicPages/PublicPageHeader.vue';
import PublicPageContent from '@/components/tailwind/publicPages/PublicPageContent.vue';
import AuthCodeInput from '@/components/tailwind/authentication/AuthCodeInput.vue';
import { logAmplitudeEvent } from '@/utils/analytics';

export default {
  name: 'SetupMFA',
  components: {
    TWLoadingSpinner,
    PublicPageTemplate,
    PublicPageHeader,
    PublicPageContent,
    AuthCodeInput,
  },
  data() {
    return {
      authCode: null,
      errorMessage: null,
      successDisplay: false,
      loading: true,
    };
  },
  async mounted() {
    await this.$store.dispatch('userMfa/CREATE').then(async () => {
      this.loading = false;
    }).catch(() => {
      // if 2fa is already verified, leave this page
      this.navigateTo('/');
    });
  },
  computed: {
    ...mapGetters({
      userMfa: 'userMfa/userMfa',
      featureEnabled: 'featureFlags/featureEnabled',
      companyMFA: 'company/companyMFA',
    }),
    infoText() {
      return this.companyMFA.mfa_enabled ? this.$t('authentication.setupInfoEnforced', { companyName: this.companyMFA.name }) : this.$t('authentication.setupInfoUnenforced');
    },
  },
  methods: {
    ...mapActions({
      logout: 'auth/LOGOUT',
      verifyMfaSetup: 'userMfa/VERIFY',
      createRecoveryCode: 'userMfa/CREATE_RECOVERY_CODE',
    }),

    async authenticate() {
      this.errorMessage = null;

      const verified = await this.verifyMfaSetup({ code: this.authCode }).then(() => {
        logAmplitudeEvent('setup_two_factor_authentication--verification-successful');
        return true;
      }).catch((error) => {
        this.errorMessage = error.response.data.message;
        logAmplitudeEvent('setup_two_factor_authentication--verification-failed');
        return false;
      });

      if (verified) {
        this.createRecoveryCode({ code: this.authCode }).then((response) => {
          this.successDisplay = true;
          // A quick delay before rerouting in which to display indications of success, otherwise it may feel jarring for the user
          setTimeout(() => this.$router.push({ name: 'MFARecoveryCode', params: { recoveryCode: response.data.code ? response.data.code : response.data.data.code } }), 800);
          logAmplitudeEvent('setup_two_factor_authentication--recovery_code_creation_successful');
        }).catch((error) => {
          this.errorMessage = error.response.data.message;
          logAmplitudeEvent('setup_two_factor_authentication--recovery_code_creation_failed');
        });
      }
    },
    setAuthCode(code) {
      this.authCode = code;

      if (this.authCode.length === 6) {
        this.authenticate();
      }
    },
    navigateTo(route) {
      this.$router.push({ path: route });
    },
  },
};
</script>
